import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { uniqueArtifacts, type AgentArtifact } from '@hub/contracts'
import { i18n } from '@/i18n'
import { ArtifactCard } from '@/components/assistant-ui/elements/artifact-card'

const COLUMN_KEYS = {
  todo: 'todo',
  'in-progress': 'inProgress',
  in_progress: 'inProgress',
  done: 'done',
  blocked: 'blocked',
} as const

function columnLabel(columnName: string): string {
  const key = (
    COLUMN_KEYS as Partial<Record<string, (typeof COLUMN_KEYS)[keyof typeof COLUMN_KEYS]>>
  )[columnName]
  return key ? i18n.t(`chat:artifacts.columns.${key}`) : columnName
}

export function artifactMeta(artifact: AgentArtifact): string {
  const isTask = artifact.kind === 'task'
  const kind = i18n.t(isTask ? 'chat:artifacts.task' : 'chat:artifacts.document')
  const action =
    artifact.status === 'error'
      ? i18n.t('chat:artifacts.failed')
      : artifact.action === 'created'
        ? i18n.t(isTask ? 'chat:artifacts.taskCreated' : 'chat:artifacts.documentCreated')
        : i18n.t(isTask ? 'chat:artifacts.taskUpdated' : 'chat:artifacts.documentUpdated')
  const column = isTask && artifact.columnName ? ` · ${columnLabel(artifact.columnName)}` : ''
  return `${kind} · ${action}${column}`
}

/** Cards for things the agent created or changed, linking into the project — one per entity. */
export function ArtifactList({
  artifacts,
  projectId,
  className,
}: {
  artifacts: AgentArtifact[]
  projectId: string
  className?: string
}) {
  const navigate = useNavigate()
  // Subscribes this list to language changes (artifactMeta reads i18n directly).
  useTranslation('chat')
  if (artifacts.length === 0) return null
  const cards = uniqueArtifacts(artifacts)

  const open = (artifact: AgentArtifact) => {
    if (artifact.status === 'writing') return
    if (artifact.kind === 'task') {
      void navigate({
        to: '/$projectId/tasks',
        params: { projectId },
        search: { task: artifact.id },
      })
    } else {
      void navigate({
        to: '/$projectId/write/$docId',
        params: { projectId, docId: artifact.id },
        // Block documents: land on the first block the agent touched.
        ...(artifact.blockIds?.[0] ? { hash: artifact.blockIds[0] } : {}),
      })
    }
  }

  return (
    <div data-slot="artifact-list" className={className ?? 'flex flex-wrap gap-2'}>
      {cards.map((artifact) => (
        <ArtifactCard
          key={artifact.stepId ?? artifact.id}
          title={
            artifact.title ||
            i18n.t(
              artifact.kind === 'task' ? 'chat:artifacts.newTask' : 'chat:artifacts.newDocument'
            )
          }
          meta={artifactMeta(artifact)}
          generating={artifact.status === 'writing'}
          words={artifact.chars ? Math.max(1, Math.round(artifact.chars / 6)) : 0}
          onClick={() => open(artifact)}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') open(artifact)
          }}
          className={artifact.status === 'error' ? 'border-red-500/40' : undefined}
        />
      ))}
    </div>
  )
}
